import { createRouter, createWebHistory } from 'vue-router';
import { nextTick } from 'vue';
import useUserStore from "../stores/useUserStore.ts";
import useAnalysisStore from "../stores/useAnalysis.ts";
import useBusinessStore from "../stores/useBusiness.ts";
import { checkUserHasRealAnalysis as checkUserHasRealAnalysisImpl } from './analysisGuard.ts';
import { 
  SUPPORT_LOCALES, 
  DEFAULT_LOCALE, 
  getLocaleFromPath, 
  removeLocaleFromPath,
  setI18nLanguage,
  addLocaleToPath,
  i18n
} from '../i18n/index.js';

// Base routes configuration (without locale prefixes)
const baseRoutes = [
  { path: '/', component: () => import('../layouts/Main.vue'), name: 'Home' },
  // auth routes
  { path: '/login', component: () => import('../sections/login/Login.vue'), name: 'Login' },
  { path: '/email-verification', component: () => import('../sections/register/EmailVerification.vue'), name: 'EmailVerification' },
  { path: '/reset-password', component: () => import('../sections/reset-password/ResetPassword.vue'), name: 'ResetPassword' },
  // checkout routes
  { 
    path: '/checkout/payment-succeeded', 
    component: () => import('../sections/checkout/PaymentSuccess.vue'),
    name: 'PaymentSuccess',
    meta: { requiresAuth: true },
  }, 
  // auth routes
  { path: '/register', component: () => import('../sections/register/Register.vue'), name: 'Register' },
  // {
  //   path: '/profile',
  //   children: [
  //     { path: '', redirect: '/profile/management' },
  //     {
  //       path: 'management',
  //       name: 'ProfileManagement',
  //       component: () => import('../sections/profile/ProfileEdit.vue'),
  //       meta: { requiresAuth: true },
  //     },
  //     {
  //       path: 'subscription',
  //       name: 'ProfileSubscription',
  //       component: () => import('../sections/profile/ProfileSubscription.vue'),
  //       meta: { requiresAuth: true },
  //     }
  //   ]
  // },
  // Quick analysis routes for authenticated users without valid subscription
  {
    path: '/quick-analysis',
    component: () => import('../layouts/QuickAnalysisLayout.vue'),
    meta: { requiresAuth: true },
    children: [
      { path: '', redirect: '/quick-analysis/type' },
      {
        path: 'type',
        name: 'QuickAnalysisType',
        component: () => import('../sections/quick-analysis/QuickAnalysisType.vue'),
        meta: { requiresAuth: true }
      },
      {
        path: 'agency-profile',
        name: 'QuickAnalysisAgencyProfile',
        component: () => import('../sections/quick-analysis/QuickAnalysisAgencyProfile.vue'),
        meta: { requiresAuth: true }
      },
      {
        path: 'setup',
        name: 'QuickAnalysisSetup',
        component: () => import('../sections/quick-analysis/QuickAnalysisSetup.vue'),
        meta: { requiresAuth: true }
      },
      {
        path: 'website-info',
        name: 'QuickAnalysisWebsiteInfo',
        component: () => import('../sections/quick-analysis/QuickAnalysisWebsiteInfo.vue'),
        meta: { requiresAuth: true }
      },
      {
        path: 'prompts',
        name: 'QuickAnalysisPrompts',
        component: () => import('../sections/quick-analysis/QuickAnalysisPrompts.vue'),
        meta: { requiresAuth: true }
      },
      {
        path: 'transition',
        name: 'AnalysisTransition',
        component: () => import('../sections/transition/AnalysisTransition.vue'),
        meta: { requiresAuth: true }
      }
      // Commented out - removed steps
      // {
      //   path: 'results',
      //   name: 'QuickAnalysisResults',
      //   component: () => import('../sections/quick-analysis/QuickAnalysisResults.vue'),
      //   meta: { requiresAuth: true }
      // },
      // {
      //   path: 'more',
      //   name: 'QuickAnalysisMore',
      //   component: () => import('../sections/quick-analysis/QuickAnalysisMore.vue'),
      //   meta: { requiresAuth: true }
      // }
    ]
  },
  // product routes
  {
    path: '/app',
    component: () => import('../layouts/AppLayout.vue'),
    meta: { requiresAuth: true, requiresValidSubscription: true },
    children: [
      { path: '', redirect: '/app/presence' },
      // Agency-specific routes
      { path: 'brands', name: 'agency-brands', component: () => import('../sections/app/AgencyBrands.vue'), meta: { requiresAuth: true, requiresValidSubscription: true, businessType: 'agency' } },
      { path: 'brand-settings', name: 'agency-brand-settings', component: () => import('../sections/business/BrandSettings.vue'), meta: { requiresAuth: true, requiresValidSubscription: true, businessType: 'agency' } },
      { path: 'business-profile', name: 'agency-business-profile', component: () => import('../sections/app/AgencyBusinessProfile.vue'), meta: { requiresAuth: true, requiresValidSubscription: true, businessType: 'agency' } },
      {
        path: 'agency-setup',
        name: 'agency-setup',
        component: () => import('../sections/app/AgencySetup.vue'),
        meta: { requiresAuth: true, requiresValidSubscription: true, businessType: 'agency', agencySetup: true },
      },
      {
        path: 'agency-config',
        name: 'agency-config',
        component: () => import('../sections/app/AgencyConfig.vue'),
        meta: { requiresAuth: true, requiresValidSubscription: true, businessType: 'agency' },
      },
      // Shared routes (both agency and product)
      {
        path: 'presence',
        name: 'Prompts',
        component: () => import('../sections/app/Prompts.vue'),
        meta: { requiresAuth: true, requiresValidSubscription: true, brandScoped: true }
      },
      {
        path: 'presence/:id',
        name: 'PromptDetail',
        component: () => import('../sections/app/PromptDetail.vue'),
        meta: { requiresAuth: true, requiresValidSubscription: true, brandScoped: true }
      },
      {
        path: 'competitors',
        name: 'Competitors',
        component: () => import('../sections/app/Competitors.vue'),
        meta: { requiresAuth: true, requiresValidSubscription: true, brandScoped: true }
      },
      {
        path: 'sources',
        name: 'Sources',
        component: () => import('../sections/app/Sources.vue'),
        meta: { requiresAuth: true, requiresValidSubscription: true, brandScoped: true }
      },
      {
        path: 'sources/:sourceId',
        name: 'SourceDetail',
        component: () => import('../sections/app/SourceDetail.vue'),
        meta: { requiresAuth: true, requiresValidSubscription: true, brandScoped: true }
      },
      {
        path: 'recommendations',
        name: 'Recommendations',
        component: () => import('../sections/app/Recommendations.vue'),
        meta: { requiresAuth: true, requiresValidSubscription: true, brandScoped: true }
      },
      {
        path: 'reports',
        name: 'Reports',
        component: () => import('../sections/app/Reports.vue'),
        meta: { requiresAuth: true, requiresValidSubscription: true, brandScoped: true }
      },
      // Settings (shared)
      {
        path: 'settings/billing',
        name: 'SettingsBilling',
        component: () => import('../sections/profile/ProfileSubscription.vue'),
        meta: { requiresAuth: true, requiresValidSubscription: true }
      },
      {
        path: 'settings/billing/pricing',
        name: 'SettingsBillingPricing',
        component: () => import('../sections/settings/SettingsBilling.vue'),
        meta: { requiresAuth: true, requiresValidSubscription: true }
      },
      {
        path: 'settings/account',
        name: 'SettingsAccount',
        component: () => import('../sections/profile/ProfileEdit.vue'),
        meta: { requiresAuth: true, requiresValidSubscription: true }
      },
      // Legacy route redirects
      {
        path: 'seo',
        redirect: (to) => addLocaleToPath('/app/presence', getLocaleFromPath(to.path))
      },
      {
        path: 'dashboard',
        redirect: (to) => addLocaleToPath('/app/presence', getLocaleFromPath(to.path))
      },
      {
        path: 'prompts',
        redirect: (to) => addLocaleToPath('/app/presence', getLocaleFromPath(to.path))
      },
      {
        path: 'prompts/:id',
        redirect: (to) => addLocaleToPath(`/app/presence/${to.params.id}`, getLocaleFromPath(to.path))
      }
    ]
  },
  // admin routes
  {
    path: '/admin',
    name: 'AdminDashboard',
    component: () => import('../sections/admin/AdminDashboard.vue'),
    meta: { requiresAuth: true, requiresAdmin: true }
  }
];

// Function to create localized routes
function createLocalizedRoutes() {
  const routes = [];

  // Add default routes (English without prefix)
  routes.push(...baseRoutes);

  // Add localized routes for non-default languages
  SUPPORT_LOCALES.forEach(locale => {
    if (locale !== DEFAULT_LOCALE) {
      baseRoutes.forEach(route => {
        const localizedRoute = {
          ...route,
          path: `/${locale}${route.path}`,
          name: route.name ? `${route.name}${locale.toUpperCase()}` : undefined,
          meta: {
            ...route.meta,
            locale
          }
        };

        // Handle nested routes
        if (route.children) {
          localizedRoute.children = route.children.map(child => ({
            ...child,
            name: child.name ? `${child.name}${locale.toUpperCase()}` : undefined,
            meta: {
              ...child.meta,
              locale
            }
          }));
        }

        routes.push(localizedRoute);
      });
    }
  });

  // Add catch-all route
  routes.push({ 
    path: '/:catchAll(.*)', 
    component: () => import('../sections/notfound/NotFound.vue'),
    name: 'NotFound'
  });

  return routes;
}

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes: createLocalizedRoutes(),
  scrollBehavior(to, from, savedPosition) {
    if (to.hash) {
      return {
        el: to.hash,
        behavior: 'smooth',
        left: 45,
      };
    }
    return { top: 0 };
  },
});

async function ensureBusinessLoaded(businessStore, userId) {
  if (!userId) return;

  if (!businessStore.hasBusiness) {
    const response = await businessStore.getUserBusiness(userId);
    if (response.success && response.business?.id) {
      await businessStore.getBrandByBusinessId(response.business.id);
    }
    return;
  }

  if (businessStore.business?.id && !businessStore.brands.length) {
    await businessStore.getBrandByBusinessId(businessStore.business.id);
  }
}

async function checkUserHasRealAnalysis() {
  const businessStore = useBusinessStore();
  const analysisStore = useAnalysisStore();
  const userStore = useUserStore();
  return checkUserHasRealAnalysisImpl({
    isAuthenticated: () => userStore.isAuthenticated,
    getUserId: () => userStore.userId,
    hasUserProfile: () => !!userStore.userProfile,
    fetchUserProfile: () => userStore.fetchUserProfile(null),
    getBusinessId: () => businessStore.business?.id ?? null,
    getUserBusiness: (id) => businessStore.getUserBusiness(id),
    getBrandByBusinessId: (id) => businessStore.getBrandByBusinessId(id),
    getRealBrandAnalysis: (id) => analysisStore.getRealBrandAnalysis(id),
  });
}

// Language detection and redirection logic
router.beforeEach(async (to, from, next) => {
  const currentLocale = getLocaleFromPath(to.path);
  await setI18nLanguage(currentLocale, false, to.path);

  const userStore = useUserStore();
  const businessStore = useBusinessStore();

  // Wait for the user store to be initialized if it hasn't been already.
  if (!userStore.isInitialized) {
    try {
      await userStore.initializeAuth();
    } catch (error) {
      next('/login');
      return;
    }
  }

  const isAuthenticated = userStore.isUserAuthenticated;
  const storeInitialized = userStore.isInitialized;

  let needsConfirmation = true;
  let hasValidSubscription = false;
  let isSubscriptionCancelled = false;

  if (isAuthenticated) {
    try {
      needsConfirmation = await userStore.needsConfirmation();
      hasValidSubscription = await userStore.hasValidSubscription();
      isSubscriptionCancelled = await userStore.isSubscriptionCancelled();
    } catch (error) {
      console.error("Error fetching user status in router guard:", error);
    }
  }

  // Get the base path without locale for easier comparison
  const basePath = removeLocaleFromPath(to.path);

  // SINGLE RULE FOR AUTHENTICATED USERS: Handle all redirects for authenticated users
  if (isAuthenticated) {
    // If user needs email verification, redirect to email verification (except if already there)
    if (needsConfirmation && basePath !== '/email-verification') {
      const redirectPath = addLocaleToPath('/email-verification', currentLocale);
      next(redirectPath);
      return;
    }

    // If user has active subscription and tries to access subscription selection, redirect to app
    // if (hasValidSubscription && !isSubscriptionCancelled && basePath === '/login/subscription-selection') {
    //   const redirectPath = addLocaleToPath('/app/dashboard', currentLocale);
    //   next(redirectPath);
    //   return;
    // }

    // If user has active subscription and tries to access quick analysis, redirect to app
    if (hasValidSubscription && !isSubscriptionCancelled && basePath.startsWith('/quick-analysis')) {
      await ensureBusinessLoaded(businessStore, userStore.userId);
      let redirectPath = addLocaleToPath('/app/presence', currentLocale);
      if (businessStore.businessType === 'agency') {
        try {
          const { getAgencyUpgradeTransitionStatus } = await import('../services/agencyWelcomeAnalysis');
          const agencyStatus = await getAgencyUpgradeTransitionStatus();
          if (agencyStatus?.status === 'pending') {
            redirectPath = addLocaleToPath('/app/agency-setup', currentLocale);
          } else {
            redirectPath = addLocaleToPath('/app/brands', currentLocale);
          }
        } catch (error) {
          console.error('Failed to check agency setup status:', error);
        }
      }
      next(redirectPath);
      return;
    }

    // Agency paid setup gate: block app until welcome configuration is accepted
    if (hasValidSubscription && !isSubscriptionCancelled) {
      await ensureBusinessLoaded(businessStore, userStore.userId);
      if (businessStore.businessType === 'agency') {
        try {
          const { getAgencyUpgradeTransitionStatus } = await import('../services/agencyWelcomeAnalysis');
          const agencyStatus = await getAgencyUpgradeTransitionStatus();
          const onAgencySetup = basePath.startsWith('/app/agency-setup');

          if (agencyStatus?.status === 'pending' && !onAgencySetup) {
            next(addLocaleToPath('/app/agency-setup', currentLocale));
            return;
          }

          if (agencyStatus?.status !== 'pending' && onAgencySetup) {
            next(addLocaleToPath('/app/presence', currentLocale));
            return;
          }
        } catch (error) {
          console.error('Failed to check agency setup status in router guard:', error);
        }
      }
    }

    // Check if Pro welcome analysis is running - redirect to /app/presence if trying to navigate away
    if (hasValidSubscription) {
      await ensureBusinessLoaded(businessStore, userStore.userId);
      if (businessStore.businessType !== 'agency') {
        try {
          const upgradeStatus = await userStore.getUpgradeTransitionStatus()
          if (
            upgradeStatus?.status === 'in_progress' &&
            !basePath.startsWith('/app/presence') &&
            !basePath.startsWith('/app/reports')
          ) {
            const redirectPath = addLocaleToPath('/app/presence', currentLocale)
            next(redirectPath)
            return
          }
        } catch (error) {
          console.error('Failed to check upgrade status in router guard:', error)
        }
      }
    }

    console.log('[Router Guard] Base path:', basePath);
    console.log('[Router Guard] Needs confirmation:', needsConfirmation);
    console.log('[Router Guard] Has valid subscription:', hasValidSubscription);
    console.log('[Router Guard] Is subscription cancelled:', isSubscriptionCancelled);
    console.log('[Router Guard] Is authenticated:', isAuthenticated);
    console.log('[Router Guard] Store initialized:', storeInitialized);
    
    // If user is on auth pages or root page (and doesn't need email verification), redirect based on subscription and real analysis
    if ((basePath === '/' || basePath === '/login' || basePath === '/register' || basePath === '/reset-password') && !needsConfirmation) {
      console.log('[Router Guard] User on auth/root page, checking subscription and analysis...');
      console.log('[Router Guard] hasValidSubscription:', hasValidSubscription, 'isSubscriptionCancelled:', isSubscriptionCancelled);
      
      // Check if subscription has expired (inactive/canceled/past_due status + freeAccessExpired)
      const profile = userStore.userProfile;
      const hasSubscriptionExpired = profile && 
        (profile.subscriptionStatus === 'inactive' || 
         profile.subscriptionStatus === 'canceled' || 
         profile.subscriptionStatus === 'past_due') && 
        profile.freeAccessExpired === true;
      
      if (hasSubscriptionExpired) {
        // User with expired subscription should go to billing
        console.log('[Router Guard] Subscription expired - redirecting to billing');
        const redirectPath = addLocaleToPath('/app/settings/billing', currentLocale);
        next(redirectPath);
        return;
      }
      
      if (hasValidSubscription && !isSubscriptionCancelled) {
        await ensureBusinessLoaded(businessStore, userStore.userId);
        let redirectPath = addLocaleToPath('/app/presence', currentLocale);
        if (businessStore.businessType === 'agency') {
          try {
            const { getAgencyUpgradeTransitionStatus } = await import('../services/agencyWelcomeAnalysis');
            const agencyStatus = await getAgencyUpgradeTransitionStatus();
            redirectPath =
              agencyStatus?.status === 'pending'
                ? addLocaleToPath('/app/agency-setup', currentLocale)
                : addLocaleToPath('/app/brands', currentLocale);
          } catch (error) {
            console.error('Failed to check agency setup status:', error);
            redirectPath = addLocaleToPath('/app/brands', currentLocale);
          }
        }
        console.log('[Router Guard] Paid user - redirecting to:', redirectPath);
        next(redirectPath);
        return;
      } else {
        // Free users: check if they have real analysis
        console.log('[Router Guard] Free user - checking for real analysis...');
        const hasRealAnalysis = await checkUserHasRealAnalysis();
        console.log('[Router Guard] Real analysis check result:', hasRealAnalysis);
        
        const redirectPath = hasRealAnalysis 
          ? addLocaleToPath('/app/presence', currentLocale)
          : addLocaleToPath('/quick-analysis/type', currentLocale);
        console.log('[Router Guard] Redirecting to:', redirectPath);
        next(redirectPath);
        return;
      }
    }
  }

  // ADMIN CHECK: Only check if route requires admin
  if (to.matched.some(record => record.meta.requiresAdmin) && isAuthenticated) {
    const profile = userStore.userProfile;
    if (profile?.role !== 'admin') {
      const redirectPath = addLocaleToPath('/app/presence', currentLocale);
      next(redirectPath);
      return;
    }
  }

  // RULE FOR UNAUTHENTICATED USERS: Check if route requires auth
  if (to.matched.some(record => record.meta.requiresAuth) && !isAuthenticated) {
    if (!storeInitialized) {
      // console.warn(`RouterGuard: Store not initialized. Redirecting to /login might cause loop if auth state is pending.`);
    }
    const redirectPath = addLocaleToPath('/login', currentLocale);
    next(redirectPath);
    return;
  }

  // SUBSCRIPTION CHECK: Only check if route specifically requires valid subscription
  if (to.matched.some(record => record.meta.requiresValidSubscription) && !hasValidSubscription && isAuthenticated) {
    // Allow access to all /app routes for free users (they get limited features)
    if (basePath.startsWith('/app')) {
      // Free users can explore all /app routes with limited functionality
      next();
      return;
    }
    
    // For non-app routes that require subscription, redirect to quick-analysis
    const redirectPath = addLocaleToPath('/quick-analysis/type', currentLocale);
    next(redirectPath);
    return;
  }

  // BUSINESS TYPE CHECK: Redirect non-agency users away from agency-specific routes
  const targetBusinessType = to.matched
    .findLast(record => record.meta.businessType)
    ?.meta.businessType;
  if (targetBusinessType && isAuthenticated) {
    await ensureBusinessLoaded(businessStore, userStore.userId);
  }
  if (targetBusinessType && businessStore.businessType !== targetBusinessType) {
    const redirectPath = addLocaleToPath('/app/presence', currentLocale);
    next(redirectPath);
    return;
  }

  next();
});

// Helper function to get localized route
export function getLocalizedRoute(routeName, locale = null) {
  // Note: This should be used within a component context where useRouter is available
  // For programmatic use, pass the router instance or current route as parameter
  return (router) => {
    const currentLocale = locale || getLocaleFromPath(router.currentRoute.value.path);
    
    if (currentLocale === DEFAULT_LOCALE) {
      return { name: routeName };
    } else {
      return { name: `${routeName}${currentLocale.toUpperCase()}` };
    }
  };
}

// Helper function to switch language for current route
export function switchLanguage(locale, router) {
  const currentPath = router.currentRoute.value.path;
  const newPath = addLocaleToPath(removeLocaleFromPath(currentPath), locale);
  return router.push(newPath);
}

export default router;
